import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import type { Entity } from '@/modules/shared/types/entity.type'

import type { TCustomerFormJson } from '../types/client-form-json.type'
import type { TCity } from '../types/client-form-promise-city.type'
import type { TCustomerFormCnpj } from '../types/client-form-promise-cnpj.type'
import type { TCustomerFormZipCode } from '../types/client-form-promise-zip-code.type'
import type { TCustomerFormTables } from '../types/client-form-tables.type'

export type CustomerFormStoreState = {
  isSaveForm?: boolean
  operationToFillValues: string
  isFillValuesAfterRequestCities: boolean
  clientSelected: Entity | undefined | null
  customerFormCities: TCity[] | undefined
  operationType: 'add' | 'edit' | 'view'
  customerFormJson: TCustomerFormJson | undefined
  customerFormCnpj?: TCustomerFormCnpj | undefined
  customerFormZipCode?: TCustomerFormZipCode | undefined
  customerFormTable: TCustomerFormTables | undefined
  cnpjOfClientToDataSearch?: string | undefined
}

export type CustomerFormStoreAction = {
  actions?: {
    setIsSaveForm: (props: boolean) => void
    setOperationToFillValues: (props: string) => void
    setCustomerFormCities: (props: TCity[] | undefined) => void
    setCustomerSelected: (props: Entity | undefined | null) => void
    setIsFillValuesAfterRequestCities: (props: boolean) => void
    setOperationType: (operation: 'add' | 'edit' | 'view') => void
    setCustomerFormJson: (props: TCustomerFormJson | undefined) => void
    setCustomerFormCnpj: (props: TCustomerFormCnpj | undefined) => void
    setCnpjOfClientToDataSearch: (props: string | undefined) => void
    setCustomerFormTable: (props: TCustomerFormTables | undefined) => void
    setCustomerFormZipCode: (props: TCustomerFormZipCode | undefined) => void
  }
}

export const CustomerFormStore = create(
  persist<CustomerFormStoreState & CustomerFormStoreAction>(
    (set) => ({
      isSaveForm: false,
      operationType: 'add',
      operationToFillValues: '',
      indexOfCityToFillCity: 0,
      customerFormCnpj: undefined,
      customerFormJson: undefined,
      clientSelected: undefined,
      customerFormTable: undefined,
      customerFormCities: undefined,
      customerFormZipCode: undefined,
      isFillValuesAfterRequestCities: false,
      cnpjOfClientToDataSearch: undefined,

      actions: {
        setIsSaveForm: (props) => set({ isSaveForm: props }),
        setCustomerSelected: (props) =>
          set({
            clientSelected: props,
          }),
        setCustomerFormJson: (props) =>
          set({
            customerFormJson: props,
          }),
        setCustomerFormCnpj: (props) =>
          set({
            customerFormCnpj: props,
          }),
        setCustomerFormTable: (props) =>
          set({
            customerFormTable: props,
          }),
        setCustomerFormCities: (props) =>
          set({
            customerFormCities: props,
          }),
        setCustomerFormZipCode: (props) =>
          set({
            customerFormZipCode: props,
          }),
        setOperationType: (operation) =>
          set({
            operationType: operation,
          }),
        setOperationToFillValues: (props) =>
          set({
            operationToFillValues: props,
          }),
        setCnpjOfClientToDataSearch: (props) =>
          set({
            cnpjOfClientToDataSearch: props,
          }),
        setIsFillValuesAfterRequestCities: (props) =>
          set({
            isFillValuesAfterRequestCities: props,
          }),
      },
    }),
    {
      name: 'client-form',
      partialize: (state) => ({
        operationType: state.operationType,
        clientSelected: state.clientSelected,
        customerFormJson: state.customerFormJson,
        customerFormTable: state.customerFormTable,
        customerFormCities: state.customerFormCities,
        operationToFillValues: state.operationToFillValues,
        isFillValuesAfterRequestCities: state.isFillValuesAfterRequestCities,
      }),
    },
  ),
)

export const useCustomerFormStore = CustomerFormStore
export const useCustomerFormStoreAction = CustomerFormStore.getState().actions
